import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { PREVENTIVE_TASK_ENDPOINT } from '../../config/endpoints';
import { PREVENTIVE_TASK_PATH } from '../../config/paths';
import {
  CLEAR_PREVENTIVE_TASK,
  CLEAR_PREVENTIVE_TASK_RESOURCE,
  CLEAR_PREVENTIVE_TASKS,
  FLAG_GETTING_PREVENTIVE_TASK_RESOURCE,
  FLAG_GETTING_PREVENTIVE_TASKS,
  FLAG_PREVENTIVE_TASK_ACTIVITY,
  FLAG_PREVENTIVE_TASK_HAS_RESOURCE,
  GET_PREVENTIVE_TASK,
  GET_PREVENTIVE_TASK_RESOURCE,
  GET_PREVENTIVE_TASKS,
} from '../types/task-schedule/PreventiveTask';

const flagGettingPreventiveTasks = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_PREVENTIVE_TASKS,
    payload: flag,
  });

const getPreventiveTasks = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingPreventiveTasks(true));

    const query = tableFilters;
    const url = `${PREVENTIVE_TASK_ENDPOINT}?${QueryString.stringify(query)}`;

    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);

    const preventiveTasks = await response.json();

    dispatch({
      type: GET_PREVENTIVE_TASKS,
      payload: preventiveTasks,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingPreventiveTasks(false));
  }
};

const getPreventiveTaskAll = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingPreventiveTasks(true));

    const query = tableFilters;
    const url = `${PREVENTIVE_TASK_ENDPOINT}/all?${QueryString.stringify(
      query,
    )}`;

    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);

    const preventiveTasks = await response.json();

    dispatch({
      type: GET_PREVENTIVE_TASKS,
      payload: preventiveTasks,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingPreventiveTasks(false));
  }
};

const clearPreventiveTasks = () => (dispatch) =>
  dispatch({
    type: CLEAR_PREVENTIVE_TASKS,
  });

const flagPreventiveTaskActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_PREVENTIVE_TASK_ACTIVITY,
    payload: flag,
  });

const getPreventiveTask =
  async ({ preventiveTaskId }) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskActivity(true));

      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const preventiveTask = await response.json();

      dispatch({
        type: GET_PREVENTIVE_TASK,
        payload: preventiveTask,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskActivity(false));
    }
  };

const clearPreventiveTask = () => (dispatch) =>
  dispatch({
    type: CLEAR_PREVENTIVE_TASK,
  });

const postPreventiveTask =
  async ({
    preventiveTask,
    weekdays,
    monthlyDays,
    monthlyRules,
    checklistItems,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskActivity(true));

      const payload = {
        preventiveTask,
        weekdays,
        monthlyDays,
        monthlyRules,
        checklistItems,
      };

      const response = await fetch(PREVENTIVE_TASK_ENDPOINT, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const savedPreventiveTask = await response.json();

      dispatch(push(`${PREVENTIVE_TASK_PATH}/${savedPreventiveTask.id}`));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskActivity(false));
    }
  };

const putPreventiveTask =
  async (
    preventiveTaskId,
    { preventiveTask, weekdays, monthlyDays, monthlyRules, checklistItems },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskActivity(true));

      const payload = {
        preventiveTask: {
          ...preventiveTask,
          id: preventiveTaskId,
        },
        weekdays,
        monthlyDays,
        monthlyRules,
        checklistItems,
      };

      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const savedPreventiveTask = await response.json();

      dispatch(push(`${PREVENTIVE_TASK_PATH}/${savedPreventiveTask.id}`));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskActivity(false));
    }
  };

const deletePreventiveTask = async (preventiveTaskId) => async (dispatch) => {
  try {
    dispatch(flagPreventiveTaskActivity(true));

    const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}`;

    const response = await fetch(url, {
      ...DEFAULT_GET_CONFIG,
      method: 'DELETE',
    });

    await isErrorResponse(response, null, dispatch);

    dispatch(push(PREVENTIVE_TASK_PATH));
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagPreventiveTaskActivity(false));
  }
};

const putPreventiveTaskResource =
  async (
    preventiveTaskId,
    { companyResourceIdList, companyResourceGroupIdList },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskActivity(true));

      const payload = {
        companyResourceIdList,
        companyResourceGroupIdList,
      };

      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}/resource`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const savedPreventiveTaskResourceList = await response.json();

      return savedPreventiveTaskResourceList;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagPreventiveTaskActivity(false));
    }
  };

const putDeactivatePreventiveTaskResource =
  async (
    preventiveTaskId,
    { companyResourceIdList, companyResourceGroupIdList },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskActivity(true));

      const payload = {
        companyResourceIdList,
        companyResourceGroupIdList,
      };

      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}/resource/deactivate`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      await response.text();

      dispatch(getPreventiveTask({ preventiveTaskId }));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskActivity(false));
    }
  };

const flagPreventiveTaskHasResource = (payload) => (dispatch) =>
  dispatch({
    type: FLAG_PREVENTIVE_TASK_HAS_RESOURCE,
    payload,
  });

const flagGettingPreventiveTaskResource = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_PREVENTIVE_TASK_RESOURCE,
    payload: flag,
  });

const getPreventiveTaskHasResource =
  async ({ preventiveTaskId }) =>
  async (dispatch) => {
    try {
      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}/has-resource`;

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const hasResource = await response.json();

      dispatch(flagPreventiveTaskHasResource(hasResource));

      return hasResource;
    } catch ({ message }) {
      toastr.error('Error', message);
      dispatch(flagPreventiveTaskHasResource(false));

      return false;
    }
  };

const getPreventiveTaskResource =
  async ({ preventiveTaskId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPreventiveTaskResource(true));

      const url = `${PREVENTIVE_TASK_ENDPOINT}/${preventiveTaskId}/resource`;

      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const preventiveTaskResourceList = await response.json();

      dispatch({
        type: GET_PREVENTIVE_TASK_RESOURCE,
        payload: preventiveTaskResourceList,
      });

      return preventiveTaskResourceList;
    } catch ({ message }) {
      toastr.error('Error', message);

      dispatch({
        type: GET_PREVENTIVE_TASK_RESOURCE,
        payload: [],
      });

      return [];
    } finally {
      dispatch(flagGettingPreventiveTaskResource(false));
    }
  };

const clearPreventiveTaskResource = () => (dispatch) =>
  dispatch({
    type: CLEAR_PREVENTIVE_TASK_RESOURCE,
  });

export {
  flagGettingPreventiveTasks,
  getPreventiveTasks,
  clearPreventiveTasks,
  flagPreventiveTaskActivity,
  getPreventiveTask,
  getPreventiveTaskAll,
  clearPreventiveTask,
  postPreventiveTask,
  putPreventiveTask,
  deletePreventiveTask,
  putPreventiveTaskResource,
  putDeactivatePreventiveTaskResource,
  flagPreventiveTaskHasResource,
  flagGettingPreventiveTaskResource,
  getPreventiveTaskHasResource,
  getPreventiveTaskResource,
  clearPreventiveTaskResource,
};
